import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@xingternal/button';
import { IconArrowDown, IconArrowRight, IconArrowUp, IconLocationPin, IconSearch } from '@xingternal/icons';
import { SingleSelect } from '@xingternal/menu';
import { usePopOver } from '@xingternal/pop-over';

import {
  LocationSuggestion,
  useBreakpoints,
  useGetLocationParamValue,
  useJobRoleAutocompletion,
  useLocationAutocompletion,
} from '@/hooks';

import { RADIUS_OPTIONS } from '../../../constants';

import * as S from './search-box-component.styles';

type Props = {
  keywordsInput: string;
  locationInput: string;
  radiusInput: number;
  isLocationDisabled?: boolean;
  locationInputRef?: React.RefObject<HTMLInputElement | undefined>;
  onChangeKeywords: (value: string) => void;
  onSelectKeywords: (value: string) => void;
  onChangeLocation: (value: string) => void;
  onSelectLocation: (item: LocationSuggestion) => void;
  onSelectRadius: (value: number) => void;
  onSubmit: () => void;
};

const countries = ['de', 'at', 'ch'];

export const SearchBoxComponent: React.FC<Props> = ({
  keywordsInput,
  locationInput,
  radiusInput,
  isLocationDisabled = false,
  locationInputRef,
  onChangeKeywords,
  onSelectKeywords,
  onChangeLocation,
  onSelectLocation,
  onSelectRadius,
  onSubmit,
}) => {
  const { formatMessage } = useIntl();
  const { isDesktop, isMobile } = useBreakpoints();
  const popOver = usePopOver();
  const { jobRoleSuggestions, fetchJobRoleSuggestions } = useJobRoleAutocompletion();
  const { locationSuggestions, fetchLocationSuggestions } = useLocationAutocompletion({
    countries,
  });
  const isDiscoEntrypoint = useGetLocationParamValue('sc_o') === 'discover_job_search_entry_point_click';

  const handleOnChangeKeywords = (value: string): void => {
    onChangeKeywords(value);
    value && fetchJobRoleSuggestions(value.trim());
  };

  const handleOnChangeLocation = (value: string): void => {
    onChangeLocation(value);
    value && fetchLocationSuggestions(value.trim());
  };

  const locationSuggestionToString = (item: LocationSuggestion): string => item.suggestion;

  const onClickRadiusButton = (): void => {
    popOver.togglePopOver();
  };

  const onClickRadiusOption = (radiusOption: number) => (): void => {
    onSelectRadius(radiusOption);
    popOver.handleHide();
  };

  const handleOnSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    onSubmit();
  };

  return (
    <S.Container>
      <S.Form onSubmit={handleOnSubmit}>
        <S.KeywordsAutoComplete
          variant="inputBar"
          value={keywordsInput}
          placeholder={formatMessage({ id: 'SE_FIELD_JOBS_KEYWORDS_HINT' })}
          suggestions={jobRoleSuggestions}
          onChange={handleOnChangeKeywords}
          onSelect={onSelectKeywords}
          data-cy="search-keywords-autocomplete"
          id={formatMessage({ id: 'SE_SUMMARY_JOBS_KEYWORDS_LABEL' })}
          icon={IconSearch}
          autoFocus={isDiscoEntrypoint}
        />
        <S.LocationFieldsButtonWrapper>
          <S.LocationFieldsWrapper>
            <S.LocationAutoComplete
              variant="inputBar"
              value={locationInput}
              placeholder={formatMessage({ id: 'SE_FIELD_JOBS_LOCATION_HINT' })}
              suggestions={locationSuggestions}
              onChange={handleOnChangeLocation}
              onSelect={onSelectLocation}
              suggestionToString={locationSuggestionToString}
              data-cy="search-location-autocomplete"
              id={formatMessage({ id: 'SE_SUMMARY_JOBS_LOCATION_LABEL' })}
              icon={IconLocationPin}
              disabled={isLocationDisabled}
              innerRef={locationInputRef}
            />

            {isMobile ? null : (
              <S.RadiusWrapper>
                <S.RadiusButton
                  size="medium"
                  type="button"
                  fontWeight="regular"
                  ellipsis
                  iconAfter
                  icon={popOver.show ? IconArrowUp : IconArrowDown}
                  onClick={onClickRadiusButton}
                  innerRef={popOver.triggerRef}
                  data-testid="search-radius-dropdown"
                  $isFilled={radiusInput !== 0}
                  disabled={isLocationDisabled}
                >
                  {radiusInput === 0 ? <FormattedMessage id="XJM_SEARCH_LOCATION_NO_RADIUS" /> : `${radiusInput} km`}
                </S.RadiusButton>

                <S.RadiusMenu
                  isInFlow
                  onOutsideClick={popOver.handleHide}
                  triggerRef={popOver.triggerRef}
                  show={popOver.show}
                  showCloseButton={false}
                >
                  {RADIUS_OPTIONS.map((radiusOption) => (
                    <SingleSelect
                      size="small"
                      type="button"
                      key={radiusOption}
                      onClick={onClickRadiusOption(radiusOption)}
                      selected={radiusOption === radiusInput}
                    >
                      <S.Label>
                        <S.RadioButton on={radiusOption === radiusInput} tabIndex="-1" readOnly />
                        {radiusOption === 0 ? (
                          <FormattedMessage id="XJM_SEARCH_LOCATION_NO_RADIUS" />
                        ) : (
                          `${radiusOption} km`
                        )}
                      </S.Label>
                    </SingleSelect>
                  ))}
                </S.RadiusMenu>
              </S.RadiusWrapper>
            )}
          </S.LocationFieldsWrapper>
          <Button
            size="medium"
            variant="primary"
            type="submit"
            data-testid="search-button"
            data-cy="search-button"
            {...(!isDesktop && { icon: IconArrowRight })}
          >
            {isDesktop ? <FormattedMessage id="MP_200_SEARCH_BUTTON" /> : null}
          </Button>
        </S.LocationFieldsButtonWrapper>
      </S.Form>
    </S.Container>
  );
};
